// Mock data for the AI Agent Workflow Platform POC

export interface Agent {
  id: string;
  name: string;
  version: string;
  category: string;
  tags: string[];
  description: string;
  owner: string;
  status: 'active' | 'deprecated';
  inputSchema: any;
  outputSchema: any;
  endpoint: string;
  sla: string;
  rateLimit: string;
  authMethod: string;
  usageCount: number;
  documentation: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'workflow_creator' | 'workflow_executor' | 'viewer' | 'developer' | 'analyst';
  permissions: string[];
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  status: 'running' | 'completed' | 'failed' | 'pending';
  startTime: string;
  endTime?: string;
  duration?: string;
  triggeredBy: string;
  steps: ExecutionStep[];
}

export interface ExecutionStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startTime?: string;
  endTime?: string;
  output?: any;
  error?: string;
}

export const mockAgents: Agent[] = [
  {
    id: 'agent-1',
    name: 'BRD Generator Agent',
    version: '1.0.0',
    category: 'Requirements',
    tags: ['requirements', 'documentation', 'sdlc'],
    description: 'Generates Business Requirements Documents from high-level product descriptions',
    owner: 'SDLC Team',
    status: 'active',
    inputSchema: { productDescription: 'string', stakeholders: 'array' },
    outputSchema: { brdDocument: 'string', sections: 'array' },
    endpoint: 'https://api.example.com/agents/brd-generator',
    sla: '99.9% uptime',
    rateLimit: '100 requests/hour',
    authMethod: 'API Key',
    usageCount: 1247,
    documentation: 'https://docs.example.com/agents/brd-generator'
  },
  {
    id: 'agent-2',
    name: 'Epic Stories Generator Agent',
    version: '1.2.0',
    category: 'Requirements',
    tags: ['agile', 'user-stories', 'sdlc'],
    description: 'Creates epic stories and user stories from BRD documents',
    owner: 'SDLC Team',
    status: 'active',
    inputSchema: { brdDocument: 'string', sprintDuration: 'number' },
    outputSchema: { epics: 'array', userStories: 'array' },
    endpoint: 'https://api.example.com/agents/epic-generator',
    sla: '99.9% uptime',
    rateLimit: '100 requests/hour',
    authMethod: 'API Key',
    usageCount: 983,
    documentation: 'https://docs.example.com/agents/epic-generator'
  },
  {
    id: 'agent-3',
    name: 'Test Cases Generator Agent',
    version: '2.0.1',
    category: 'Testing',
    tags: ['testing', 'qa', 'automation'],
    description: 'Generates comprehensive test cases from user stories and requirements',
    owner: 'QA Team',
    status: 'active',
    inputSchema: { userStories: 'array', testingStrategy: 'string' },
    outputSchema: { testCases: 'array', coverage: 'number' },
    endpoint: 'https://api.example.com/agents/test-generator',
    sla: '99.5% uptime',
    rateLimit: '50 requests/hour',
    authMethod: 'OAuth 2.0',
    usageCount: 756,
    documentation: 'https://docs.example.com/agents/test-generator'
  },
  {
    id: 'agent-4',
    name: 'Code Generator Agent',
    version: '1.5.0',
    category: 'Development',
    tags: ['code-generation', 'development', 'automation'],
    description: 'Generates boilerplate code and implementation from technical specifications',
    owner: 'Dev Team',
    status: 'active',
    inputSchema: { specifications: 'string', language: 'string', framework: 'string' },
    outputSchema: { code: 'string', files: 'array' },
    endpoint: 'https://api.example.com/agents/code-generator',
    sla: '99.9% uptime',
    rateLimit: '200 requests/hour',
    authMethod: 'API Key',
    usageCount: 2134,
    documentation: 'https://docs.example.com/agents/code-generator'
  },
  {
    id: 'agent-5',
    name: 'Code Review Agent',
    version: '1.1.0',
    category: 'Development',
    tags: ['code-review', 'quality', 'best-practices'],
    description: 'Performs automated code review and suggests improvements',
    owner: 'Dev Team',
    status: 'active',
    inputSchema: { code: 'string', language: 'string', rules: 'array' },
    outputSchema: { issues: 'array', suggestions: 'array', score: 'number' },
    endpoint: 'https://api.example.com/agents/code-review',
    sla: '99.5% uptime',
    rateLimit: '150 requests/hour',
    authMethod: 'API Key',
    usageCount: 1876,
    documentation: 'https://docs.example.com/agents/code-review'
  },
  {
    id: 'agent-6',
    name: 'Documentation Generator Agent',
    version: '1.0.2',
    category: 'Documentation',
    tags: ['documentation', 'api-docs', 'technical-writing'],
    description: 'Generates technical documentation from code and specifications',
    owner: 'Documentation Team',
    status: 'active',
    inputSchema: { code: 'string', format: 'string' },
    outputSchema: { documentation: 'string', diagrams: 'array' },
    endpoint: 'https://api.example.com/agents/doc-generator',
    sla: '99.0% uptime',
    rateLimit: '100 requests/hour',
    authMethod: 'API Key',
    usageCount: 654,
    documentation: 'https://docs.example.com/agents/doc-generator'
  }
];

export const mockUsers: User[] = [
  {
    id: 'user-1',
    username: 'admin',
    email: 'admin@example.com',
    role: 'admin',
    permissions: ['view_agents', 'execute_workflows', 'edit_workflows', 'register_agents', 'admin_system']
  },
  {
    id: 'user-2',
    username: 'developer',
    email: 'dev@example.com',
    role: 'developer',
    permissions: ['view_agents', 'execute_workflows', 'edit_workflows']
  },
  {
    id: 'user-3',
    username: 'analyst',
    email: 'analyst@example.com',
    role: 'analyst',
    permissions: ['view_agents', 'execute_workflows']
  }
];

